// BL579 M4 — detect the managed vector substrate (pgvector) from a parsed
// cloudgrid.yaml, client-side.
//
// Why client-side: the `GET /api/v2/runtimes/:id` detail response carries no
// capability declarations (no `requires:` / `needs:` echo), and BL579 M4 is a
// CLI-only deliverable (no server changes). The single source of truth for
// "does this entity declare vector?" is therefore the project's own
// cloudgrid.yaml, parsed and probed with the SAME validators the deployer uses
// (`validateVectorRequires` + `needsToRequires`) so the CLI never disagrees
// with the platform about what was declared.
//
// Env-var name: the engine-explicit `VECTOR_PGVECTOR_URL` per the founder-
// finalized contract in `packages/shared/src/cloudgrid-yaml-env-contract.md`
// (2026-06-25). NOTE — the live M2-wiring deployer currently injects the older
// flat `PGVECTOR_URL` (`pool-pgvector.ts` `PGVECTOR_ENV_VAR`); that divergence
// is a server-side follow-up (out of scope for this CLI PR). The CLI surfaces
// the contract name so `info` + the dev sandbox agree with the documented
// developer contract.

import {
  validateVectorRequires,
  needsToRequires,
  SUPPORTED_VECTOR_ENGINES,
} from '../../../shared/src/validate.js';
import type { CloudGridYaml, NeedsBlock } from '../../../shared/src/types.js';

/** The engine-explicit env var the entity's code reads at runtime. */
export const VECTOR_PGVECTOR_ENV_VAR = 'VECTOR_PGVECTOR_URL';

/** v1 default engine — the only engine the platform provisions today. */
export const VECTOR_DEFAULT_ENGINE = SUPPORTED_VECTOR_ENGINES[0]; // 'pgvector'

export interface VectorCapability {
  /** The backing engine (`pgvector` in v1). */
  engine: string;
  /** The env var the entity reads the connection URL from. */
  envVar: string;
  /** Optional embedding dimensionality hint, when the yaml pinned one. */
  dim?: number;
}

type LooseYaml = Partial<CloudGridYaml> & {
  services?: Record<string, { needs?: NeedsBlock } | undefined>;
};

/**
 * Detect the managed vector substrate across BOTH vocabularies (v1 `requires:`,
 * v2 `needs:`) and BOTH scopes (top-level + per-service). Vector is an
 * entity-level capability — the platform provisions ONE pgvector role per
 * entity — so a declaration anywhere in the yaml means the entity has it.
 *
 * Returns the capability (engine defaults to `pgvector` when not explicitly
 * pinned) or `null` when no vector substrate is declared. Tolerant: a malformed
 * yaml or a malformed single declaration resolves toward `null` rather than
 * throwing, so callers (e.g. `grid info`) never crash on a half-written
 * file.
 */
export function detectVectorCapability(yaml: unknown): VectorCapability | null {
  if (!yaml || typeof yaml !== 'object') return null;
  const y = yaml as LooseYaml;

  // Collect every `requires:`-shaped array to probe: the v1 top-level list, the
  // v2 top-level needs (translated onto the v1 wire), and each service's v2
  // needs override.
  const candidates: CloudGridYaml['requires'][] = [];
  try {
    if (Array.isArray(y.requires)) candidates.push(y.requires);
    if (y.needs) candidates.push(needsToRequires(y.needs));
    if (y.services && typeof y.services === 'object') {
      for (const svc of Object.values(y.services)) {
        if (svc && typeof svc === 'object' && svc.needs) {
          candidates.push(needsToRequires(svc.needs));
        }
      }
    }
  } catch {
    return null;
  }

  for (const requires of candidates) {
    try {
      const parsed = validateVectorRequires(requires);
      if (parsed) {
        return {
          engine: parsed.engine ?? VECTOR_DEFAULT_ENGINE,
          envVar: VECTOR_PGVECTOR_ENV_VAR,
          ...(parsed.dim !== undefined ? { dim: parsed.dim } : {}),
        };
      }
    } catch {
      // A single malformed candidate must not mask a valid declaration
      // elsewhere — keep probing the remaining scopes.
      continue;
    }
  }

  return null;
}
